import { EditOutlined, PlusOutlined, PlusSquareOutlined, SearchOutlined } from "@ant-design/icons";
import { ur } from "@faker-js/faker";
import { QueryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Card, Col, Divider, Flex, Form, Input, Layout, message, Modal, notification, Radio, Row, Select, Space, Table, TableProps, Tag, Typography } from "antd";
import React, { useEffect, useState, useRef, useMemo } from 'react';
import AdminApiService from "src/api/AdminApiService";
import { ApiError } from "src/api/ApiResultHandler";
import CustomTag from "src/components/CustomTag";
import AutomationType from "src/consts/AutomationType";
import IotModel from "src/consts/IotModel";
import AddAzureDeviceDTO from "src/models/AddAzureDeviceDTO";
import AzureIoTEdgeDeviceDTO from "src/models/AzureIoTEdgeDeviceDTO";
import BinInfoDTO from "src/models/BinInfoDTO";
import { getAllAzureIoTEdgeDevicesQueryOptions } from "src/queries/useGetAllAzureIoTEdgeDevicesQuery";
import { getBinsQueryOptions } from "src/queries/useGetBinsQuery";
import { getEnumDictionary, getEnumKeys } from "src/utils/enumUtils";
import { useDebouncedCallback } from "use-debounce";
import { NotAssignedTag } from "./NotAssignedTag";
import EnterpriseApiService from "src/api/EnterpriseApiService";
import { queryOptions } from "src/queries/v4QueryOptions/queryOptions";
import IotDeviceAndMinimalBinInfoDTO from "src/models/IotDeviceAndMinimalBinInfoDTO";
import UpdateIoTDeviceDTO from "src/models/UpdateIoTDeviceDTO";
import IotDeviceDTO from "src/models/IotDeviceDTO";
import BinInfoForIotDeviceDTO from "src/models/BinInfoForIotDeviceDTO";
import { layout } from "../dashboard/BinStatusPage/BinStatsPage";
import ECDeviceDTO from "src/models/ECDeviceDTO";
import { getUnusedAzureIotDevicesOptions } from "src/queries/getUnusedAzureIotDevicesOptions";
import { getAllECDevicesOptions } from "src/queries/getAllECDevicesOptions";
import { getGrowerIdsOptions } from "src/queries/getGrowerIdsOptions";


const { Title, Text } = Typography;

const iotDeviceIdPreviewText = (formValues: AddAzureDeviceDTO) => {
    if (formValues == null) {
        return "no data";
    }
    if (formValues.ioTModelType == null) {
        return 'no IoT model selected';
    }
    if (formValues.ioTModelType == IotModel.Reliagate) {
        return `RG-65-${formValues.serialNumber}`;
    }
    if (formValues.ioTModelType == IotModel.Compulab_IOTG_IMX8PLUS) {
        return `CL-IMX8P-${formValues.serialNumber}`;
    }
    return `unimplemented - ${formValues.serialNumber}`;
}

const useUpdateIotDeviceMutation = (iotDeviceId: number) => {
    return useMutation({
        mutationFn: async (updateIotDeviceDTO: UpdateIoTDeviceDTO) => {
            return AdminApiService.updateIotDevice(iotDeviceId, updateIotDeviceDTO);
        },
        onError(error, variables, context) {
            console.error("Error updating IoT device in DB", error);
            message.error(`Error updating IoT device in DB. ${(error as ApiError)?.errorDetails?.detail}`, 3);
        },
    })
}

const addIotDeviceFormId = "add-iot-device-form";
const IoTDevices = () => {
    const [form] = Form.useForm<AddAzureDeviceDTO>();
    const [updateForm] = Form.useForm<UpdateIoTDeviceDTO>();
    const [showAddDeviceModal, setShowAddDeviceModal] = useState(false);
    const [submitButtonEnabled, setSubmitButtonEnabled] = useState(false);
    const [showDeviceDetailModal, setShowDeviceDetailModal] = useState(false);
    const [selectDevice, setSelectedDevice] = useState< (AzureIoTEdgeDeviceDTO & Partial<BinInfoDTO>) | undefined>();
    const queryClient = useQueryClient();
    const mutateIotDevice = useUpdateIotDeviceMutation(selectDevice?.iotDeviceId!);
    const formValues = Form.useWatch([], { form, preserve: true});
    const growersQuery = useQuery({...getGrowerIdsOptions(false)});

    const getAllUnusedAzureIoTDevicesQuery = useQuery({...getUnusedAzureIotDevicesOptions()});
    const unusedAzureIoTDevices = getAllUnusedAzureIoTDevicesQuery.data;

    const getAllECDevicesQuery = useQuery({...getAllECDevicesOptions()});

    const iotDataExtraInfoOptions = () => queryOptions({
        queryKey: ['iot-data-extra-info'],
        queryFn: (q) => EnterpriseApiService.getIoTDevicesFromDB(q.signal),
    });

    const getBinsQuery = useQuery({...iotDataExtraInfoOptions(), refetchOnWindowFocus: false });
    
    const [nameSearch, setNameSearch] = useState("");
    const nameSearchDelayedValue = useDebouncedCallback((value) => {
        setNameSearch(value);
    }, 500);
    
    const getAllAzureIoTEdgeDevicesQuery = useQuery({...getAllAzureIoTEdgeDevicesQueryOptions,  enabled: true, refetchOnWindowFocus: false});

    const handleAddDeviceButtonClicked = () => {
        setShowAddDeviceModal(true);
    }

    const handleCloseAddDeviceModal = () => {
        setShowAddDeviceModal(false);
    }

    const handleOpenDeviceDetailModal = (deviceId: string) => {
        const device = combinedData?.find(d => d.deviceId == deviceId);
        if(device != null){
            setSelectedDevice(device);
            setShowDeviceDetailModal(true);
        }else{
            notification.error({ message: 'We could not find the device specified for some reason. Contact support' });
        }
    }

    const handleCloseDeviceDetailmodal = () => {
        setShowDeviceDetailModal(false);
        setSelectedDevice(undefined);
    }

    const handleDownloadProvisionsClicked = async () => {
        try {

            if (selectDevice?.deviceId != null) {
                const url = `${window.location.protocol}//${window.location.host}/api/admin/getAzureIotEdgeDeviceProvisions`;
                const requestURL = new URL(url);
                requestURL.searchParams.set('deviceId', selectDevice.deviceId);
    
                // Fetch the zip file from the API
                const response = await fetch(requestURL.toString(), {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/zip',
                    },
                });
    
                if (!response.ok) {
                    throw new Error(`Failed to download: ${response.status} ${response.statusText}`);
                }

                // Convert response to Blob
                const blob = await response.blob();
    
                // Create a download link and trigger click
                const downloadUrl = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = downloadUrl;
                a.download = `provisions_${selectDevice.deviceId}.zip`; // Set the download file name
                document.body.appendChild(a);
                a.click();
    
                // Cleanup
                URL.revokeObjectURL(downloadUrl);
                document.body.removeChild(a);
            }
        } catch (err) {
            console.error("Error downloading provisions:", err);
        }
    };
    
    const unusedECDevices = getAllECDevicesQuery.data
        ?.filter(ec => {
            return selectDevice?.ecDeviceID === ec.id
                || getBinsQuery.data?.find(bin => bin.iot?.ecDeviceID === ec.id) == null
                ;
        });

    useEffect( () => {
        if(selectDevice != null){
            updateForm.setFieldsValue({
                serialNumber: selectDevice.serialNumber,
                azureDeviceId: selectDevice.deviceId,
                ecDeviceId: selectDevice.ecDeviceID,
                ioTModel: selectDevice.iotModel ?? undefined,
                growerId: selectDevice.grower?.growerID ?? null,
            });
        }else {
            updateForm.resetFields();
        }
    }, [selectDevice]);

    const combinedData: Array<AzureIoTEdgeDeviceDTO & Partial<BinInfoDTO>> = useMemo(() => getAllAzureIoTEdgeDevicesQuery.data
    ?.map(device => {
        const bin = getBinsQuery.data?.find(iot => iot.iot?.azureDeviceId === device.deviceId);
        return {...device, 
            growerName: device.grower?.growerName, 
            automationType: bin?.bin?.automationType,
            name: bin?.bin?.binName,
            iotDeviceId: bin?.iot?.iotDeviceId,
            ecDeviceId: bin?.iot?.ecDeviceID,
            ecDeviceID: bin?.iot?.ecDeviceID,
            azureDeviceId: bin?.iot?.azureDeviceId,
            ioTModel: bin?.iot?.iotModel,
            iotModel: bin?.iot?.iotModel,
            serialNumber: bin?.iot?.serialNumber,
        }
    }) ?? [], [getBinsQuery.data, getAllAzureIoTEdgeDevicesQuery.data]);

    const columns: TableProps<AzureIoTEdgeDeviceDTO & Partial<BinInfoDTO>>['columns'] = [
        {
            title: "Bin Name",
            dataIndex: "name",
            render(value, record, index) {
                return value ?? <NotAssignedTag />;
            },
        },
        {
            title: "Organization",
            dataIndex: "growerName",
            render(value, record, index) {
                return value ?? <NotAssignedTag />;
            },
        },
        {
            title: "Device Type",
            dataIndex: "iotModel",
            render(value, record, index) {
                const iotmodel = record.iotModel;
                if (iotmodel == null) {
                    return <NotAssignedTag />;
                }
                if (iotmodel === IotModel.Reliagate) {
                    return "Reliagate"
                }
                else if (iotmodel === IotModel.Compulab_IOTG_IMX8PLUS) {
                    return "Compulab";
                }
                else {
                    return iotmodel;
                }
            },
        },
        {
            title: 'Device ID',
            key: 'deviceId',
            dataIndex: 'deviceId',
            render: (text) => <a onClick={ ()=>{ handleOpenDeviceDetailModal(text) }}>{text}</a>,
        },
        {
            title: "System Type",
            dataIndex: "automationType",
            render(value, record, index) {
                return value ?? <NotAssignedTag />;
            },
        },
        {
            title: "Status",
            render(value, record, index) {
                if (record?.ecDeviceID == null) {
                    return <Tag color="red">Not Eligible</Tag>;
                }
                if (record.name != null) {
                    return <Tag color="green">Assigned</Tag>;
                }
                return <Tag color="yellow">Eligible</Tag>;
            },
        },
    ];

    const iotModelOptions =[    
        { value: -1, label: 'Select an IoT Model' },
        ...(getEnumDictionary(IotModel) ?? []).map( (g) => (
            { value: g.value, label: g.label }
        ))
    ]

    useEffect( () => {
        if(getAllAzureIoTEdgeDevicesQuery.isError){
            notification.error({
                message: "Could not submit IoT device",
                description: getAllAzureIoTEdgeDevicesQuery.error?.errorDetails?.detail ?? "Contact support"
            })
        }
    }, [getAllAzureIoTEdgeDevicesQuery.dataUpdatedAt]);

    const onAddNewDeviceFinish = async (values: AddAzureDeviceDTO) => {
        try{
            const result = await AdminApiService.addAzureIotEdgeDevice(values);
            notification.success({ message: 'IoT Device successfully submitted' });
            queryClient.invalidateQueries({queryKey: getAllAzureIoTEdgeDevicesQueryOptions.queryKey});
            queryClient.invalidateQueries({queryKey: iotDataExtraInfoOptions().queryKey});
            handleCloseAddDeviceModal();
        }catch(err){
            notification.error({
                message: "Could not submit IoT device",
                description: err?.errorDetails?.detail ?? "Contact support"
            });
        }
    }

    const filteredIoTDevices = useMemo(() => {
        if (nameSearch == null || nameSearch == ""){
            return combinedData ?? [];
        }

        const filtered = combinedData
        ?.filter( (d) => {
            return d.deviceId?.toLowerCase()?.includes(nameSearch.toLowerCase())
            || d.growerName?.toLowerCase()?.includes(nameSearch.toLowerCase())
            || d.name?.toLowerCase()?.includes(nameSearch.toLowerCase());
        });
        return filtered;
    }, [nameSearch, combinedData]);

    return(
        <Layout.Content>
            <Row gutter={16}>
                <Col xs={24} lg={4}>
                        <Button type="primary" block
                        icon={<PlusOutlined />}
                        onClick={handleAddDeviceButtonClicked}
                        >
                            Add device
                        </Button>
                </Col>
                <Col xs={24} lg={20}>
                    <Flex gap={24} vertical>
                        <Input style={{width: "64ch"}} prefix={<SearchOutlined />} placeholder="Search IoT Devices"
                        onChange={(evt) => nameSearchDelayedValue(evt.target.value)}
                        />

                        <Table 
                        scroll={{ y: "55vh"}}
                        columns={columns} 
                        dataSource={filteredIoTDevices} 
                        loading={getAllAzureIoTEdgeDevicesQuery.isFetching || getBinsQuery.isFetching}
                        pagination={false}
                        />
                    </Flex>
                </Col>
            </Row>

            <Modal
            open={showDeviceDetailModal}
            onCancel={handleCloseDeviceDetailmodal}
            okButtonProps={{ style: { display:'none' } }}
            cancelButtonProps={{ style: { display:'none' } }}
            destroyOnClose
            width="600px"
            title={selectDevice?.deviceId}
            >
                <Row justify={'center'}>
                    <Button
                    onClick={handleDownloadProvisionsClicked}
                    >Download Provisions</Button>
                </Row>

                <Divider>Edit</Divider>

                {selectDevice && <>
                <Form<UpdateIoTDeviceDTO> 
                form={updateForm}
                labelCol={{ span: 8 }} 
                wrapperCol={{ span: 16 }} 
                initialValues={selectDevice}
                onFinish={async (values) => {
                    mutateIotDevice.mutate(values, {
                        onSuccess(data, variables, context) {
                            queryClient.invalidateQueries({queryKey: getAllAzureIoTEdgeDevicesQueryOptions.queryKey});
                            queryClient.invalidateQueries({queryKey: iotDataExtraInfoOptions().queryKey});
                            queryClient.invalidateQueries({queryKey: getAllECDevicesOptions().queryKey});
                            message.success(`IoT Device info updated`);
                            setShowDeviceDetailModal(false);
                        },
                    });
                  }}
                    >
                    <Form.Item<UpdateIoTDeviceDTO>  name="serialNumber" label="IoT Serial Number">
                        <Input disabled readOnly />
                    </Form.Item>
                    <Form.Item<UpdateIoTDeviceDTO> name="azureDeviceId" label="Azure Device ID">
                                            <Select disabled style={{ maxWidth: '360px', overflow: "auto", maxHeight: "600px"} } optionFilterProp="children" showSearch >
                        <Select.Option value={null}>
                            No Device
                        </Select.Option>
                        {
                            unusedAzureIoTDevices?.map((id) => (
                                <Select.Option key={id} value={id}>
                                    {id}
                                </Select.Option>
                            ))
                        }
                    </Select>
                    </Form.Item>
                    <Form.Item<UpdateIoTDeviceDTO> name="ecDeviceId" label="EC Device ID">
                        <Select style={{ maxWidth: '360px', overflow: "auto", maxHeight: "600px"} } optionFilterProp="children" showSearch >
                        <Select.Option value={null}>
                            No Device
                        </Select.Option>
                        {
                            unusedECDevices?.map((ecDevice) => (
                                <Select.Option key={ecDevice.id ?? ''} value={ecDevice.id ?? ''}>
                                    {ecDevice.displayName} - {ecDevice.id}
                                </Select.Option>
                            ))
                        }
                    </Select>
                    </Form.Item>
                    <Form.Item<UpdateIoTDeviceDTO> name="ioTModel" label="IoT Model">
                    <Radio.Group disabled>
                        <Radio value={IotModel.Reliagate}>Reliagate</Radio>
                        <Radio value={IotModel.Compulab_IOTG_IMX8PLUS}>Compulab</Radio>
                    </Radio.Group>
                    </Form.Item>
                    <Form.Item<UpdateIoTDeviceDTO>
                    name="growerId"
                    label="Organization"
                    >
                        <Select
                        disabled={growersQuery.isLoading}
                        style={{ width: '360px', overflow: "auto" }}
                        placeholder="Select organization"
                        showSearch
                        optionFilterProp="children"
                        loading={growersQuery.isLoading}
                        >
                            <Select.Option
                            key={"none"}
                            value={null}
                            >
                                No Organization
                            </Select.Option>
                            {growersQuery.data?.map(grower => (
                                <Select.Option
                                key={grower.growerID}
                                value={grower.growerID}
                                >
                                    {grower.growerName}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Flex justify="end">
                        <Button loading={mutateIotDevice.isLoading} type="primary" htmlType="submit">Save</Button>
                    </Flex>
                </Form>
                </>}
            </Modal>

            <Modal
            open={showAddDeviceModal}
            onCancel={handleCloseAddDeviceModal}
            footer={null}
            destroyOnClose
            >
                <Form
                form={form}
                id={addIotDeviceFormId}
                onFinish={onAddNewDeviceFinish}>
                    <Title level={3}>Add a new device</Title>

                    <Row>
                        <Form.Item<AddAzureDeviceDTO>
                        name={'ioTModelType'}
                        label={'Pick your IoT Model type'}
                        rules={[ 
                            {
                                validator: (_, value) => {
                                    if(value === -1){
                                        setSubmitButtonEnabled(false);
                                    }else if(value === 0 || value === 1){
                                        setSubmitButtonEnabled(true);
                                    }
                                }
                            }
                        ]}
                        >
                            <Select 
                            options={iotModelOptions} 
                            style={{
                                width: '200px',
                            }}/>
                        </Form.Item>
                    </Row>
                    <br/>
                    <Row>
                        <Form.Item<AddAzureDeviceDTO>
                        name={'serialNumber'}
                        label={'Enter the device\'s serial number'}
                        rules={[
                            {required: true, message: 'Please enter the device\'s serial number'}
                        ]}
                        >
                            <Input placeholder="i.e Y122HNA3469..."/>
                        </Form.Item>
                    </Row>
                    <br/>

                    <br/>
                    <Row>
                        <Form.Item<AddAzureDeviceDTO>
                        name="growerId"
                        label="Grower"
                        >
                            <Select
                                disabled={growersQuery.isLoading}
                                style={{ width: '360px', overflow: "auto" }}
                                placeholder="Select grower"
                                loading={growersQuery.isLoading}
                            >
                                {growersQuery.data?.map(grower => (
                                    <Select.Option
                                        key={grower.growerID}
                                        value={grower.growerID}
                                    >
                                        {grower.growerName}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Row>

                    <Row justify={'center'}>
                        <Col>
                        <Typography.Text copyable>{iotDeviceIdPreviewText(formValues)}</Typography.Text>
                        </Col>
                    </Row>
                    <br />

                    <Row justify={'center'}>
                        <Form.Item>
                            <Button onClick={() => onAddNewDeviceFinish(form.getFieldsValue())} disabled={!submitButtonEnabled} type='primary' form={addIotDeviceFormId} htmlType="submit">Submit</Button>
                        </Form.Item>
                    </Row>
                </Form>
            </Modal>
        </Layout.Content>
    )
}

export default IoTDevices;

